import {
	BaseQueryFn,
	createApi,
	FetchArgs,
	fetchBaseQuery,
	FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";

import { ZodSchema } from "zod";

import { PARSED_ENV } from "@/constants/common";
import { RootState } from "@/redux";
import { campaignConfigTooLargeError, isCampaignConfigTooLarge } from "@/utils/campaignConfigGuard";

interface FetchArgsWithSchemas extends FetchArgs {
	requestSchema?: ZodSchema;
	responseSchema?: ZodSchema;
}

export const API_REDUCER_KEY = "api";

const baseQuery = fetchBaseQuery({
	baseUrl: PARSED_ENV.VITE_API_URL + "/api",
	prepareHeaders: (headers: Headers, { getState }) => {
		const token = (getState() as RootState).auth.token;

		if (token) {
			headers.set("authorization", `Bearer ${token}`);
		}

		return headers;
	},
});

const baseQueryWithValidation: BaseQueryFn<
	FetchArgsWithSchemas,
	unknown,
	FetchBaseQueryError
> = async (args, api, extraOptions) => {
	// Reject an oversized campaignConfig in the browser instead of firing a
	// request the server can only answer with an opaque 413 PayloadTooLargeError.
	const body = args.body as { campaignConfig?: unknown } | undefined;

	if (isCampaignConfigTooLarge(body?.campaignConfig)) {
		return { error: campaignConfigTooLargeError };
	}

	try {
		args.requestSchema?.parse(args.body);
	} catch (err) {
		console.error(err);
	}

	const result = await baseQuery(args, api, extraOptions);

	try {
		// await args.responseSchema?.parseAsync(result.data);
	} catch (err) {
		console.error(`"${args.url}" response schema validation error\n`, err);
	}

	return result;
};

export const allTags = [
	"ApiHealth",
	"RepairRun",
	"Users",
	"Roles",
	"Apollos",
	"People",
	"Job",
	"Indeed",
	"Companies",
	"CsvEnrichment",
	"LiEvent",
	"LiPeople",
	"LiCompanyActivity",
	"LiProfileVisits",
	"LiProfile",
	"ParallelAi",
	"OpenAi",
	"List",
	"Northdata",
	"Lemlist",
	"CustomEvent",
	"ListPerson",
	"CampaignRouting",
	"Score",
	"JobTitleScreening",
	"Company",
	"Me",
	"Blacklist",
	"ListLog",
	"Map",
	"Requests",
	"Cron",
	"LiPost",
	"Usage",
	"Column",
	"MapPreview",
	"ListTemplates",
	"Campaign",
	"TableConfig",
	"Credits",
	"ProfileTracker",
	"ProfileTrackerFile",
	"TrackerListImport",
	"PositiveReplyTracking",
	"TrackerOrganization",
	"TrackerTableConfig",
	"LiEventEntries",
	"Features",
	"Subscription",
	"MyConnections",
	"LinkedinEnrichmentSettings",
	"LiSn",
	"Profiles",
	"ProfilesWithLiSn",
	"ColumnTemplate",
	"ProfilesWithAdminPermissions",
	"WebsiteScrape",
	"ApiKeys",
	"Webhooks",
	"Webhook",
	"Hubspot",
	"ScoreCriteria",
	"Slots",
	"Email",
	"Schedule",
	"ListAssignedToCampaign",
	"MessageTemplate",
	"FundingMa",
	"SignalConfig",
	"CompanySignalConfig",
	"TrackerSignals",
	"DeepResearchPrompt",
	"DeepResearchPinnedFact",
	"ImportedConversation",
	"KnowledgeDbIcp",
	"KnowledgeDbProduct",
	"KnowledgeDbCaseStudy",
	"KnowledgeDbObjection",
	"KnowledgeDbObjectionCategory",
	"KnowledgeDbSetting",
	"KnowledgeDbPendingSync",
	"SupportDashboard",
	"AdminFeatures",
	"Notifications",
	"NotificationPreferences",
	"Contact",
	"ContactSettings",
	"EmailSuppression",
	"SavedFilter",
	"SearchProfilePreset",
	"ListOverviewPinnedTab",
	"UserUiFlow",
	"SearchDefaults",
	"Organization",
	"FeatureOnboarding",
	"OnboardingTour",
	"KnowledgeSetup",
	"SenderCopywritingPreference",
	"SignalPriorityHistory",
	"Zoho",
	"Pipedrive",
	"Dynamics",
	"Salesforce",
	"Crm",
	"CompanyEnrich",
	"ScoreCompanyCategories",
	"ListPreview",
	"CopywritingSettings",
	"CopywritingFramework",
	"CopywritingOrchestration",
	"LanguageRule",
	"FrameworkEffectiveness",
	"ReviewSession",
	"Dashboard",
	"Feed",
	"FeedCounts",
	"KnowledgeDbReviewStatus",
	"CopywritingReviewStatus",
	"TemplatesReviewStatus",
	"HelpArticles",
	"HelpAdminArticles",
	"HelpConversations",
	"McpConnections",
	"OnboardingIcp",
	"OutreachInstructionTemplate",
	"Tickets",
];

export const api = createApi({
	reducerPath: API_REDUCER_KEY,
	baseQuery: baseQueryWithValidation,
	tagTypes: allTags,
	endpoints: () => ({}),
});
